/**
 * BlockEditDialog — right-click a block in the 3D viewer → this modal opens
 * with the block's current fields and lets the user patch grade / lithology
 * / rock_type / domain / material / weathering.
 *
 * Sends `PATCH /api/projects/{pid}/block-models/{mid}/blocks/{bid}` and calls
 * `onSaved(patchedBlock)` so the viewer can update in place.
 */
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { X, Save, Loader2, Package } from "lucide-react";
import { toast } from "sonner";

export default function BlockEditDialog({ open, block, projectId, blockModelId, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open && block) {
      setForm({
        grade: block.grade ?? 0,
        lithology: block.lithology ?? "",
        rock_type: block.rock_type ?? "",
        domain: block.domain ?? "",
        material: block.material ?? block.material_class ?? "",
        weathering: block.weathering ?? "",
        notes: block.notes ?? "",
      });
      setErr(null);
    }
  }, [open, block]);

  if (!open || !block) return null;
  const blockId = block.id || block._id;

  const save = async () => {
    if (!blockId) { setErr("Block has no id"); return; }
    setBusy(true); setErr(null);
    try {
      await api.patch(`/projects/${projectId}/block-models/${blockModelId}/blocks/${blockId}`,
        { ...form, grade: parseFloat(form.grade) || 0 });
      toast.success("Block updated");
      onSaved?.({ ...block, ...form, grade: parseFloat(form.grade) || 0 });
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setErr(msg);
      toast.error(`Failed: ${msg}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm grid place-items-center" onClick={onClose} data-testid="block-edit-dialog">
      <div
        className="bg-neutral-900 border border-white/10 rounded-lg shadow-2xl w-[440px] max-w-[92vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Package size={16} className="text-lime-600" />
            <div>
              <div className="font-semibold text-[13px]">Edit Block</div>
              <div className="text-[10px] opacity-60 font-mono">
                {block.x !== undefined ? `(${block.x?.toFixed(1)}, ${block.y?.toFixed(1)}, ${block.z?.toFixed(1)})` : blockId}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="h-6 w-6 grid place-items-center rounded hover:bg-white/10"><X size={14}/></button>
        </div>
        <div className="p-4 space-y-3 text-[12px]">
          <Field label="Grade" testid="block-edit-grade">
            <input type="number" step="0.001"
              value={form.grade}
              onChange={(e) => setForm({ ...form, grade: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5 font-mono" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Lithology" testid="block-edit-lithology">
              <input value={form.lithology} onChange={(e) => setForm({ ...form, lithology: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5" />
            </Field>
            <Field label="Rock Type" testid="block-edit-rock-type">
              <input value={form.rock_type} onChange={(e) => setForm({ ...form, rock_type: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Domain" testid="block-edit-domain">
              <input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5" />
            </Field>
            <Field label="Weathering" testid="block-edit-weathering">
              <input value={form.weathering} onChange={(e) => setForm({ ...form, weathering: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5" />
            </Field>
          </div>
          <Field label="Material Class" testid="block-edit-material">
            <input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5" />
          </Field>
          <Field label="Notes" testid="block-edit-notes">
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5 resize-none" />
          </Field>
          {err && <div className="text-[11px] text-red-400 border border-red-500/30 rounded p-2 bg-red-500/10">{err}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-white/10">
          <button onClick={onClose} className="px-3 py-1.5 text-[11px] border border-white/10 rounded hover:border-white/25" data-testid="block-edit-cancel">
            Cancel
          </button>
          <button onClick={save} disabled={busy}
            className="flex items-center gap-2 px-4 py-1.5 text-[11px] bg-lime-600 hover:bg-lime-700 rounded text-white font-semibold disabled:opacity-50"
            data-testid="block-edit-save"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wider opacity-70">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
